<script setup>
import { computed, ref } from 'vue';

import BaseInfoPopup from '@/components/Base/BaseInfoPopup.vue';
import { useDataFormat } from '@/composables/useDataFormat.js';

const { transformArray } = useDataFormat();

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  fieldKey: {
    type: String,
    required: true,
  },
  validationRules: {
    type: String,
    default: 'required',
  },
  answers: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:answers']);
const tempAnswers = ref();

const getRadioOptions = computed(() => {
  return transformArray(props.question.input_values);
});

const minDate = computed(() => {
  return props.question.input_options && props.question.input_options.minDate
    ? new Date(props.question.input_options.minDate)
    : null;
});

const maxDate = computed(() => {
  return props.question.input_options && props.question.input_options.maxDate
    ? new Date(props.question.input_options.maxDate)
    : null;
});

const shouldRenderSubQuestion = (vraag) => {
  return (
    (vraag.input_trigger_value && vraag.input_trigger_value.includes(props.answers[props.question.voogd_field_token])) ||
    props.question.input_type === 'none'
  );
};

const valAnswers = computed({
  get() {
    return props.answers;
  },
  set(val) {
    emit('update:answers', val);
  },
});

const valueRef = computed({
  get() {
    return props.answers[props.fieldKey] !== undefined ? props.answers[props.fieldKey] : '';
  },
  set(val) {
    updateAnswersArray(val);
    emit('update:answers', tempAnswers.value);
  },
});

const updateAnswersArray = (val) => {
  tempAnswers.value = Object.assign({}, props.answers);
  tempAnswers.value[props.fieldKey] = typeof val === 'undefined' ? null : val;
};
</script>

<template>
  <div class="questionBlock">
    <!-- Question zonder input -->
    <template v-if="question.input_type === 'none' && question.parent_id === 0">
      <div class="row">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="col-12 col-lg-12 questionWrapper mb-1"
          v-html="question.question"
        />
      </div>
    </template>

    <!-- Radio button question -->
    <template v-if="question.input_type === 'radio' || question.input_type === 'checkbox'">
      <div class="row">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="col-11 col-md-12 label-text order-md-1"
          v-html="question.question"
        />
        <div class="col-1 order-md-3 question-info-icon">
          <BaseInfoPopup :content="question.infotekst" />
        </div>
        <div class="col-12 col-sm-8 col-md-7 col-lg-8 col-xl-7 order-md-2">
          <BaseMultiRadio
            v-model="valueRef"
            :options="getRadioOptions"
            :name="question.name"
            validation="required"
          />
        </div>
      </div>
    </template>

    <!-- Select option question -->
    <template v-if="question.input_type === 'select'">
      <div class="row">
        <div
          class="col-11 col-md-12 label-text order-md-1"
          v-html="question.question"
        />
        <div class="col-1 order-md-3 question-info-icon">
          <BaseInfoPopup :content="question.infotekst" />
        </div>
        <div class="col-12 col-sm-8 col-md-7 col-lg-8 col-xl-7 order-md-2">
          <BaseDropDown
            v-model="valueRef"
            :options="getRadioOptions"
            :name="question.name"
            :validation="validationRules"
          />
        </div>
      </div>
    </template>

    <!-- Textarea question -->
    <template v-if="question.input_type === 'textarea' || question.input_type === 'text'">
      <div class="row">
        <div
          class="col-11 col-md-12 label-text order-md-1"
          v-html="question.question"
        />
        <div class="col-1 order-md-3 question-info-icon">
          <BaseInfoPopup :content="question.infotekst" />
        </div>
        <div class="col-12 col-sm-8 col-md-7 col-lg-8 col-xl-7 order-md-2">
          <BaseInput
            v-model="valueRef"
            :name="question.name"
            :type="question.input_type"
            :validation="validationRules"
            maxlength="1024"
          />
        </div>
      </div>
    </template>

    <!-- Datepicker question -->
    <template v-if="question.input_type === 'date'">
      <div class="row">
        <div
          class="col-11 col-md-12 label-text order-md-1"
          v-html="question.question"
        />
        <div class="col-1 order-md-3 question-info-icon">
          <BaseInfoPopup :content="question.infotekst" />
        </div>
        <div class="col-12 col-sm-8 col-md-7 col-lg-8 col-xl-7 order-md-2">
          <BaseDatePicker
            v-model="valueRef"
            :name="question.name"
            :min-date="minDate"
            :max-date="maxDate"
            :validation="validationRules"
            place-holder
            clearable
          />
        </div>
      </div>
    </template>

    <template v-if="typeof question.children !== 'undefined' && question.children.length > 0">
      <template v-for="child in question.children">
        <BaseDynamicQuestion
          v-if="shouldRenderSubQuestion(child)"
          :key="child.voogd_field_token"
          v-model:answers="valAnswers"
          :field-key="child.voogd_field_token"
          :question="child"
        />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.label-text {
  font-family: var(--font-medium);
  margin-bottom: 1rem;
}

.question-info-icon {
  padding-top: 1.5rem;
  @media (max-width: 768px) {
    padding: 0 1rem 1rem 0;
    align-self: flex-end;
  }
}
</style>
